/**
 * 3글자 국가코드(IOC/ISO alpha-3) → 2글자 코드(ISO alpha-2) 매핑
 * flagcdn.com에서 국기 SVG를 가져오기 위해 alpha-2 코드 필요
 *
 * 배드민턴 국제대회에서 자주 사용되는 국가 위주로 포함
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  // 아시아
  KOR: "kr", JPN: "jp", CHN: "cn", TPE: "tw", HKG: "hk",
  INA: "id", MAS: "my", THA: "th", SGP: "sg", VIE: "vn",
  IND: "in", PAK: "pk", SRI: "lk", PHI: "ph", MYA: "mm",
  LAO: "la", CAM: "kh", BRU: "bn", MDV: "mv", NEP: "np",
  BAN: "bd", MGL: "mn", UZB: "uz", KAZ: "kz", KGZ: "kg",
  TJK: "tj", TKM: "tm", MAC: "mo",

  // 중동
  IRN: "ir", IRQ: "iq", KSA: "sa", UAE: "ae", QAT: "qa",
  KUW: "kw", BRN: "bh", OMA: "om", JOR: "jo", LBN: "lb",
  SYR: "sy", YEM: "ye", ISR: "il", PSE: "ps",

  // 유럽
  DEN: "dk", GBR: "gb", ENG: "gb-eng", SCO: "gb-sct",
  GER: "de", FRA: "fr", ESP: "es", ITA: "it", NED: "nl",
  BEL: "be", SUI: "ch", AUT: "at", SWE: "se", NOR: "no",
  FIN: "fi", POL: "pl", CZE: "cz", SVK: "sk", HUN: "hu",
  ROU: "ro", BUL: "bg", CRO: "hr", SRB: "rs", SLO: "si",
  BIH: "ba", MKD: "mk", MNE: "me", ALB: "al", GRE: "gr",
  TUR: "tr", POR: "pt", IRL: "ie", LUX: "lu", EST: "ee",
  LAT: "lv", LTU: "lt", UKR: "ua", BLR: "by", RUS: "ru",
  GEO: "ge", ARM: "am", AZE: "az", CYP: "cy", MLT: "mt",
  ISL: "is", AND: "ad", MON: "mc", LIE: "li", SMR: "sm",

  // 오세아니아
  AUS: "au", NZL: "nz", FIJ: "fj", PNG: "pg", SAM: "ws",
  TON: "to", VAN: "vu", SOL: "sb", GUM: "gu",

  // 북미
  USA: "us", CAN: "ca", MEX: "mx", CUB: "cu", JAM: "jm",
  PUR: "pr", DOM: "do", HAI: "ht", TTO: "tt", BAR: "bb",
  BAH: "bs", GUA: "gt", HON: "hn", CRC: "cr", PAN: "pa",
  ESA: "sv", NCA: "ni", BIZ: "bz", BER: "bm",

  // 남미
  BRA: "br", ARG: "ar", CHI: "cl", COL: "co", PER: "pe",
  VEN: "ve", ECU: "ec", BOL: "bo", URU: "uy", PAR: "py",
  GUY: "gy", SUR: "sr",

  // 아프리카
  RSA: "za", NGR: "ng", GHA: "gh", KEN: "ke", ETH: "et",
  EGY: "eg", MAR: "ma", TUN: "tn", ALG: "dz", CMR: "cm",
  CIV: "ci", SEN: "sn", UGA: "ug", TAN: "tz", RWA: "rw",
  MOZ: "mz", ZIM: "zw", NAM: "na", BOT: "bw", MRI: "mu",
  SEY: "sc", MAD: "mg", ANG: "ao", COD: "cd", CGO: "cg",
  GAB: "ga", MLI: "ml", BUR: "bf", NIG: "ne", BEN: "bj",
  TOG: "tg", SLE: "sl", LBR: "lr", GAM: "gm", GUI: "gn",
  CPV: "cv", STP: "st", COM: "km", DJI: "dj", ERI: "er",
  SOM: "so", SSD: "ss", SUD: "sd", LBA: "ly", CHA: "td",
  CAF: "cf", GEQ: "gq", ZAM: "zm", MWI: "mw", LES: "ls",
  SWZ: "sz",
};

/**
 * 3글자 코드가 유효한 국가코드인지 확인
 * (영문 대문자 3글자이고 매핑이 존재하는 경우)
 */
export const isCountryCode = (code: string): boolean => {
  if (!code || code.length !== 3) return false;
  return /^[A-Z]{3}$/.test(code) && code in ALPHA3_TO_ALPHA2;
};

/**
 * 두 팀명 모두 3글자 국가코드인지 확인
 */
export const areBothCountryCodes = (teamA: string, teamB: string): boolean => {
  return isCountryCode(teamA.trim()) && isCountryCode(teamB.trim());
};

/**
 * 3글자 국가코드에서 flagcdn.com 국기 이미지 URL 생성
 * w80 = 가로 80px (Retina 대응으로 넉넉하게)
 */
export const getFlagUrl = (alpha3Code: string, width: number = 80): string => {
  const alpha2 = ALPHA3_TO_ALPHA2[alpha3Code.trim().toUpperCase()];
  if (!alpha2) return "";
  return `https://flagcdn.com/w${width}/${alpha2}.png`;
};

/**
 * 3글자 국가코드에서 SVG 국기 URL 생성 (더 고품질)
 */
export const getFlagSvgUrl = (alpha3Code: string): string => {
  const alpha2 = ALPHA3_TO_ALPHA2[alpha3Code.trim().toUpperCase()];
  if (!alpha2) return "";
  return `https://flagcdn.com/${alpha2}.svg`;
};
